import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateStaff } from '@/hooks/useStaff';
import { useKitchenSpecialties } from '@/hooks/useKitchenSpecialties';
import { useZones } from '@/hooks/useZones';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RefreshCw } from 'lucide-react';
import { validatePassword, generateSecurePassword } from '@/lib/password-validation';
import type { Staff } from '@/types/entities';

interface EditStaffDialogProps {
  staff: Staff;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditStaffDialog({ staff, open, onOpenChange, onSuccess }: EditStaffDialogProps) {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    role: staff.role,
    isActive: staff.isActive,
    newPassword: '',
    specialtyId: staff.specialtyId || '',
    zoneIds: Array.isArray(staff.zoneIds) ? staff.zoneIds : staff.zoneId ? [staff.zoneId] : [],
    receiptHeader: staff.receiptHeader || '',
    receiptFooter: staff.receiptFooter || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState<string[]>([]);

  const updateStaffMutation = useUpdateStaff(staff.id, staff.restaurantId);
  const { data: specialties } = useKitchenSpecialties(staff.restaurantId);
  const { data: zones } = useZones(staff.restaurantId);

  // Reset form data when staff changes
  useEffect(() => {
    setFormData({
      role: staff.role,
      isActive: staff.isActive,
      newPassword: '',
      specialtyId: staff.specialtyId || '',
      zoneIds: Array.isArray(staff.zoneIds) ? staff.zoneIds : staff.zoneId ? [staff.zoneId] : [],
      receiptHeader: staff.receiptHeader || '',
      receiptFooter: staff.receiptFooter || '',
    });
    setPasswordStrength([]);
  }, [staff]);

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(12);
    setFormData({ ...formData, newPassword });
    handlePasswordChange(newPassword);
  };

  const handlePasswordChange = (password: string) => {
    if (password) {
      const validation = validatePassword(password);
      setPasswordStrength(validation.errors);
    } else {
      setPasswordStrength([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate
    const newErrors: Record<string, string> = {};

    if (!formData.role) {
      newErrors['role'] = t('validation.required');
    }

    // Validate password if provided
    if (formData.newPassword) {
      const passwordValidation = validatePassword(formData.newPassword);
      if (!passwordValidation.isValid && passwordValidation.errors[0]) {
        newErrors['newPassword'] = t(passwordValidation.errors[0]);
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const updateData: any = {
        role: formData.role,
        isActive: formData.isActive,
        specialtyId:
          formData.role === 'kitchen' && formData.specialtyId ? formData.specialtyId : null,
        zoneIds:
          formData.role === 'server' && formData.zoneIds.length > 0
            ? formData.zoneIds
            : formData.role === 'server'
              ? []
              : [], // Explicitly set to empty array for non-server roles to clear zone assignments
      };

      // Only include password if it's been set
      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }

      // Include receipt header/footer for printer role
      if (formData.role === 'printer') {
        updateData.receiptHeader = formData.receiptHeader || null;
        updateData.receiptFooter = formData.receiptFooter || null;
      }

      await updateStaffMutation.mutateAsync(updateData);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setErrors({
        form: error instanceof Error ? error.message : t('error.generic'),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('staff.editStaff')}</DialogTitle>
            <DialogDescription>{t('staff.editStaffDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t('staff.username')}</Label>
              <Input id="username" value={staff.username} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">
                {t('staff.usernameCannotChange') || 'Username cannot be changed'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {t('staff.newPassword') || 'New Password (optional)'}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="newPassword"
                  type="text"
                  value={formData.newPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, newPassword: e.target.value });
                    handlePasswordChange(e.target.value);
                  }}
                  disabled={updateStaffMutation.isPending}
                  placeholder={t('staff.leaveBlankToKeep') || 'Leave blank to keep current'}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleGeneratePassword}
                  disabled={updateStaffMutation.isPending}
                  title="Generate secure password"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              {passwordStrength.length > 0 && (
                <div className="text-xs text-muted-foreground space-y-1">
                  {passwordStrength.map((error, index) => (
                    <p key={index} className="text-amber-600">
                      • {t(error)}
                    </p>
                  ))}
                </div>
              )}
              {errors['newPassword'] && (
                <p className="text-sm text-destructive">{errors['newPassword']}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t('staff.role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(value: 'server' | 'kitchen' | 'printer') =>
                  setFormData({ ...formData, role: value })
                }
                disabled={updateStaffMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('staff.selectRole')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="server">{t('user.server')}</SelectItem>
                  <SelectItem value="kitchen">{t('user.kitchen')}</SelectItem>
                  <SelectItem value="printer">{t('user.printer')}</SelectItem>
                </SelectContent>
              </Select>
              {errors['role'] && <p className="text-sm text-destructive">{errors['role']}</p>}
            </div>

            {/* Specialty selector - only for kitchen role */}
            {formData.role === 'kitchen' && specialties && specialties.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="specialty">{t('settings.kitchenSpecialties')}</Label>
                <Select
                  value={formData.specialtyId || 'none'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, specialtyId: value === 'none' ? '' : value })
                  }
                  disabled={updateStaffMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('settings.noSpecialtyAssigned')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('settings.noSpecialtyAssigned')}</SelectItem>
                    {specialties
                      .filter((s) => s.isActive)
                      .map((specialty) => (
                        <SelectItem key={specialty.id} value={specialty.id}>
                          {i18n.language === 'ar' ? specialty.nameAr : specialty.nameFr}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Zone selector - only for server role */}
            {formData.role === 'server' && zones && zones.length > 0 && (
              <div className="space-y-2">
                <Label>{t('staff.zones') || 'Zones'}</Label>
                <div className="space-y-2 border rounded-md p-3 max-h-48 overflow-y-auto">
                  {zones
                    .filter((z) => z.isActive)
                    .map((zone) => (
                      <div key={zone.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`zone-${zone.id}`}
                          checked={formData.zoneIds.includes(zone.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                zoneIds: [...formData.zoneIds, zone.id],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                zoneIds: formData.zoneIds.filter((id) => id !== zone.id),
                              });
                            }
                          }}
                          disabled={updateStaffMutation.isPending}
                        />
                        <Label
                          htmlFor={`zone-${zone.id}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {i18n.language === 'ar' ? zone.nameAr : zone.nameFr}
                        </Label>
                      </div>
                    ))}
                  {zones.filter((z) => z.isActive).length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {t('staff.noZonesAvailable') || 'No zones available'}
                    </p>
                  )}
                </div>
                {formData.zoneIds.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('staff.selectZonesHint') || 'Select one or more zones for this server'}
                  </p>
                )}
              </div>
            )}

            {/* Receipt header and footer - only for printer role */}
            {formData.role === 'printer' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="receiptHeader">{t('staff.receiptHeader')}</Label>
                  <Textarea
                    id="receiptHeader"
                    value={formData.receiptHeader}
                    onChange={(e) => setFormData({ ...formData, receiptHeader: e.target.value })}
                    disabled={updateStaffMutation.isPending}
                    placeholder={t('staff.receiptHeaderPlaceholder')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="receiptFooter">{t('staff.receiptFooter')}</Label>
                  <Textarea
                    id="receiptFooter"
                    value={formData.receiptFooter}
                    onChange={(e) => setFormData({ ...formData, receiptFooter: e.target.value })}
                    disabled={updateStaffMutation.isPending}
                    placeholder={t('staff.receiptFooterPlaceholder')}
                    rows={3}
                  />
                </div>
              </>
            )}

            <div className="flex items-center justify-between">
              <Label>{t('assistant.active') || 'Active'}</Label>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                disabled={updateStaffMutation.isPending}
              />
            </div>

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateStaffMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateStaffMutation.isPending}>
              {updateStaffMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
